import React, { useState, useEffect } from "react";
import {
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalCloseButton,
  ModalBody,
  ModalFooter,
  Button,
  VStack,
  HStack,
  Box,
  Text,
  Center,
  Spinner,
  Divider,
  Select,
  Input,
} from "@chakra-ui/react";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faShare, faUserMinus } from "@fortawesome/free-solid-svg-icons";
import { normalizeRole } from "../config/roles";
import {
  getSharedUserRoleFromMap,
  normalizeShareRole,
  SHARE_ROLES,
} from "../utils/shareAccess";
import { UserListPicker, UserListManagerModal } from '../userLists';

const sameId = (a, b) =>
  a != null &&
  b != null &&
  String(a).toLowerCase() === String(b).toLowerCase();

export const SHARE_ROLE_SELECT_OPTIONS = [
  { value: SHARE_ROLES.VIEW, label: "Viewer (read-only)" },
  { value: SHARE_ROLES.WRITE, label: "Editor (document only)" },
  { value: SHARE_ROLES.ADMIN, label: "Admin (full row access)" },
];

/**
 * Shared modal for sharing/unsharing a row with other users.
 *
 * Props:
 *   onShare         – (user, role) => void  role: 'view' | 'write' | 'admin'
 *   onRoleChange    – optional (userId, newRole) => void for existing shares
 *   sharedUserRoles – optional Record<userId, role string>
 *   updatingRoleId  – optional string while role save in flight
 *   defaultShareRole – default for new shares (default 'admin')
 */
const ShareRowModal = ({
  isOpen,
  onClose,
  title,
  loading,
  users,
  currentUserId,
  alreadySharedIds,
  onShare,
  onShareMany,
  onRemove,
  removingId,
  footerExtra,
  allowAllRoles = false,
  sharedUserRoles = {},
  onRoleChange,
  updatingRoleId = null,
  defaultShareRole = SHARE_ROLES.ADMIN,
  ownerId = null,
}) => {
  const [pendingRoleByUserId, setPendingRoleByUserId] = useState({});
  const [searchQuery, setSearchQuery] = useState("");
  const [managerOpen, setManagerOpen] = useState(false);
  const [pendingFromLists, setPendingFromLists] = useState([]); // [{ user, role, source }]

  useEffect(() => {
    if (!isOpen) {
      setPendingRoleByUserId({});
      setSearchQuery("");
      setManagerOpen(false);
      setPendingFromLists([]);
    }
  }, [isOpen]);

  const isAlreadyShared = (id) =>
    alreadySharedIds.has(String(id).toLowerCase()) ||
    [...alreadySharedIds].some((x) => sameId(x, id));

  const trimmedQuery = searchQuery.trim().toLowerCase();
  const matchesSearch = (u) => {
    if (!trimmedQuery) return true;
    const name = (u?.name || "").toLowerCase();
    const email = (u?.email || "").toLowerCase();
    return name.includes(trimmedQuery) || email.includes(trimmedQuery);
  };

  const pendingFromListIds = new Set(
    pendingFromLists.map((e) => String(e.user.id).toLowerCase()),
  );
  const isInPendingFromLists = (id) =>
    pendingFromListIds.has(String(id).toLowerCase());

  const available = (users || []).filter(
    (u) =>
      !sameId(u.id, currentUserId) &&
      !sameId(u.id, ownerId) &&
      !isAlreadyShared(u.id) &&
      !isInPendingFromLists(u.id) &&
      (allowAllRoles || !(normalizeRole(u?.role) === 'doctor' || normalizeRole(u?.role) === 'admin')) &&
      matchesSearch(u),
  );

  const visibleAlreadySharedIds = Array.from(alreadySharedIds)
    .filter((sharedId) => !sameId(sharedId, ownerId))
    .filter((sharedId) => {
      if (!trimmedQuery) return true;
      const u = (users || []).find((x) => sameId(x.id, sharedId));
      if (u) return matchesSearch(u);
      return String(sharedId).toLowerCase().includes(trimmedQuery);
    });

  const ownerUser = ownerId
    ? (users || []).find((u) => sameId(u.id, ownerId))
    : null;

  /** Display label for a user; avoid showing raw UUID when name/email are missing */
  const userLabel = (u) => {
    if (!u) return "Unknown";
    const nameOrEmail =
      (u.name && String(u.name).trim()) ||
      (u.email && String(u.email).trim());
    if (nameOrEmail) return nameOrEmail;
    if (u.id) return `User (${u.id.slice(0, 8)}…)`;
    return "Unknown user";
  };

  const userSubtext = (u) => {
    if (!u || !u.email) return null;
    if (u.name && String(u.name).trim() !== u.email) return u.email;
    return null;
  };

  return (
    <Modal isOpen={isOpen} onClose={onClose} size="lg" scrollBehavior="inside">
      <ModalOverlay />
      <ModalContent maxH="85vh">
        <ModalHeader>
          <HStack>
            <FontAwesomeIcon icon={faShare} />
            <Text>{title}</Text>
          </HStack>
        </ModalHeader>
        <ModalCloseButton />
        <ModalBody overflowY="auto">
          {loading ? (
            <Center py={4}>
              <Spinner />
            </Center>
          ) : (
            <VStack align="stretch" spacing={2}>
              <UserListPicker
                contextUsers={users}
                excludeUserIds={
                  new Set([
                    ...Array.from(alreadySharedIds || []).map((x) => String(x).toLowerCase()),
                    currentUserId ? String(currentUserId).toLowerCase() : '',
                    ownerId ? String(ownerId).toLowerCase() : '',
                  ].filter(Boolean))
                }
                defaultRole={defaultShareRole}
                applyButtonLabel="Share"
                onApply={async (expanded) => {
                  // Skip clinical-role recipients the host excluded, then share
                  // immediately with all eligible entries in a single batch.
                  // Looping per-user onShare hits a stale-state bug since the
                  // host's handleShare reads share_grants from a closure that
                  // doesn't update between awaits.
                  const eligible = expanded.filter(
                    (e) =>
                      allowAllRoles || !(normalizeRole(e.user?.role) === 'doctor' || normalizeRole(e.user?.role) === 'admin'),
                  );
                  if (eligible.length === 0) return;
                  if (typeof onShareMany === "function") {
                    await onShareMany(eligible);
                  } else {
                    for (const e of eligible) {
                      await onShare(e.user, e.role);
                    }
                  }
                }}
                onOpenManager={() => setManagerOpen(true)}
              />

              <UserListManagerModal
                isOpen={managerOpen}
                onClose={() => setManagerOpen(false)}
                userPool={users || []}
              />

              {pendingFromLists.length > 0 && (
                <Box mb={3}>
                  <Text fontSize="xs" fontWeight="semibold" color="gray.600" textTransform="uppercase" mb={1}>
                    Pending from lists
                  </Text>
                  <VStack align="stretch" spacing={1}>
                    {pendingFromLists.map((entry) => (
                      <HStack key={entry.user.id}>
                        <Box flex="1">
                          <Text fontSize="sm">
                            {entry.user.name || entry.user.email || entry.user.id}{' '}
                            <Text as="span" fontSize="xs" color="gray.500">
                              — from list "{entry.source.listName}"
                            </Text>
                          </Text>
                        </Box>
                        <Select
                          size="sm"
                          w="130px"
                          value={entry.role}
                          onChange={(e) => {
                            const next = e.target.value;
                            setPendingFromLists((prev) =>
                              prev.map((p) =>
                                p.user.id === entry.user.id ? { ...p, role: next } : p,
                              ),
                            );
                          }}
                        >
                          {SHARE_ROLE_SELECT_OPTIONS.map((opt) => (
                            <option key={opt.value} value={opt.value}>{opt.label}</option>
                          ))}
                        </Select>
                        <Button
                          size="sm"
                          colorScheme="blue"
                          leftIcon={<FontAwesomeIcon icon={faShare} />}
                          onClick={() => {
                            onShare(entry.user, entry.role);
                            setPendingFromLists((prev) => prev.filter((p) => p.user.id !== entry.user.id));
                          }}
                        >
                          Share
                        </Button>
                      </HStack>
                    ))}
                  </VStack>
                  <Divider my={3} />
                </Box>
              )}

              <Input
                placeholder="Search users by name or email"
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                size="sm"
              />
              <Text fontSize="xs" color="gray.600">
                Choose a permission level before sharing. Viewer: read document
                only. Editor: edit document only. Admin: edit schedule, delete,
                share, and document (like owner).
              </Text>
              {ownerId && (
                <>
                  <HStack
                    justify="space-between"
                    align="flex-start"
                    p={2}
                    borderWidth={1}
                    borderRadius="md"
                    borderColor="gray.200"
                    bg="gray.50"
                    spacing={2}
                  >
                    <Box flex={1} minW={0}>
                      <Text fontWeight="medium">
                        {ownerUser
                          ? userLabel(ownerUser)
                          : `User (${String(ownerId).slice(0, 8)}…)`}
                      </Text>
                      {ownerUser && userSubtext(ownerUser) && (
                        <Text fontSize="sm" color="gray.600">
                          {userSubtext(ownerUser)}
                        </Text>
                      )}
                    </Box>
                    <Select
                      size="sm"
                      maxW="200px"
                      flexShrink={0}
                      value="owner"
                      isDisabled
                    >
                      <option value="owner">Owner</option>
                    </Select>
                  </HStack>
                  <Divider mb={2} />
                </>
              )}
              {/* Currently shared — users who already have access */}
              {visibleAlreadySharedIds.length > 0 && (
                <>
                  <Text
                    fontSize="sm"
                    fontWeight="semibold"
                    color="gray.700"
                  >
                    Currently shared (change role or remove)
                  </Text>
                  <VStack align="stretch" spacing={2}>
                    {visibleAlreadySharedIds.map((sharedId) => {
                      const u = (users || []).find((x) => sameId(x.id, sharedId));
                      const label = u
                        ? userLabel(u)
                        : sharedId.length > 8
                          ? `User (${sharedId.slice(0, 8)}…)`
                          : sharedId;
                      const isRemoving = sameId(removingId, sharedId);
                      const mapped = getSharedUserRoleFromMap(
                        sharedUserRoles,
                        sharedId,
                      );
                      const currentRole =
                        mapped != null ? mapped : SHARE_ROLES.ADMIN;
                      return (
                        <HStack
                          key={sharedId}
                          justify="space-between"
                          align="flex-start"
                          p={2}
                          borderWidth={1}
                          borderRadius="md"
                          borderColor="green.200"
                          bg="green.50"
                          spacing={2}
                        >
                          <Box flex={1} minW={0}>
                            <Text fontWeight="medium">{label}</Text>
                            {u && userSubtext(u) && (
                              <Text fontSize="sm" color="gray.600">
                                {userSubtext(u)}
                              </Text>
                            )}
                          </Box>
                          {typeof onRoleChange === "function" && (
                            <Select
                              size="sm"
                              maxW="200px"
                              flexShrink={0}
                              value={currentRole}
                              isDisabled={sameId(updatingRoleId, sharedId)}
                              onChange={(e) =>
                                onRoleChange(
                                  sharedId,
                                  normalizeShareRole(e.target.value),
                                )
                              }
                            >
                              {SHARE_ROLE_SELECT_OPTIONS.map((opt) => (
                                <option key={opt.value} value={opt.value}>
                                  {opt.label}
                                </option>
                              ))}
                            </Select>
                          )}
                          <Button
                            size="sm"
                            colorScheme="red"
                            variant="outline"
                            leftIcon={<FontAwesomeIcon icon={faUserMinus} />}
                            isLoading={isRemoving}
                            onClick={() => onRemove(u || { id: sharedId })}
                          >
                            Remove
                          </Button>
                        </HStack>
                      );
                    })}
                  </VStack>
                  <Divider mb={2} />
                </>
              )}

              {/* Users available to share with */}
              {available.map((u) => {
                const roleVal =
                  pendingRoleByUserId[u.id] ??
                  defaultShareRole ??
                  SHARE_ROLES.ADMIN;
                return (
                  <HStack
                    key={u.id}
                    justify="space-between"
                    align="flex-start"
                    p={2}
                    borderWidth={1}
                    borderRadius="md"
                    spacing={2}
                  >
                    <Box flex={1} minW={0}>
                      <Text fontWeight="medium">{userLabel(u)}</Text>
                      {userSubtext(u) && (
                        <Text fontSize="sm" color="gray.600">
                          {userSubtext(u)}
                        </Text>
                      )}
                    </Box>
                    <Select
                      size="sm"
                      maxW="200px"
                      flexShrink={0}
                      value={normalizeShareRole(roleVal)}
                      onChange={(e) =>
                        setPendingRoleByUserId((prev) => ({
                          ...prev,
                          [u.id]: e.target.value,
                        }))
                      }
                    >
                      {SHARE_ROLE_SELECT_OPTIONS.map((opt) => (
                        <option key={opt.value} value={opt.value}>
                          {opt.label}
                        </option>
                      ))}
                    </Select>
                    <Button
                      size="sm"
                      colorScheme="blue"
                      leftIcon={<FontAwesomeIcon icon={faShare} />}
                      onClick={() =>
                        onShare(
                          u,
                          normalizeShareRole(
                            pendingRoleByUserId[u.id] ?? defaultShareRole,
                          ),
                        )
                      }
                    >
                      Share
                    </Button>
                  </HStack>
                );
              })}

              {!loading &&
                available.length === 0 &&
                visibleAlreadySharedIds.length === 0 && (
                  <Text color="gray.500" py={4}>
                    {trimmedQuery
                      ? `No users match "${searchQuery.trim()}".`
                      : "No other users to share with, or already shared with all."}
                  </Text>
                )}
            </VStack>
          )}
        </ModalBody>
        <ModalFooter>
          {footerExtra}
          <Button onClick={onClose}>Close</Button>
        </ModalFooter>
      </ModalContent>
    </Modal>
  );
};

export default ShareRowModal;
